/**
 * NegocioNuevoWizardView -- Wizard 7 pasos para crear un negocio nuevo.
 * Artefacto: #/negocios/nuevo
 * Mision: PANEL-OPS-V4-001 T1
 *
 * Persistencia: sessionStorage clave `nx_wizard_new_biz` entre recargas.
 * Validacion progresiva por paso.
 *
 * @module presentation/views/NegocioNuevoWizardView
 */

import { AppConfig } from '../../config.js';
import { BusinessApiClient } from '../../infrastructure/api/BusinessApiClient.js';
import { leerCatalogo, opcionesConValorActual, MENSAJE_SIN_CATALOGO } from '../../application/negocios/catalogo.js';
// ONB-COLOMBIA-UNICA: departamentos y ciudades salen de un solo lugar (antes, copia de 111 ciudades).
import { DEPARTAMENTOS, CIUDADES } from '../../application/negocios/colombia.js';
// FND-T1: la descripcion de marca para los fondos (opcional en el alta; se completa luego en la ficha).
import { cuerpoDeMarca, marcaVacia, loQueFalta, motivoDeLaMarcaDelNegocio, ETIQUETAS, EJEMPLOS } from '../../application/negocios/marcaDelNegocio.js';
import { coloresDominantes, colorEnPunto, colorPrincipalDelAlta, deHex } from '../../application/negocios/coloresDelLogo.js';
import { porQueNoSePuedeSubir } from '../../application/negocios/archivoDeApariencia.js';
import { AparienciaApiClient } from '../../infrastructure/api/AparienciaApiClient.js';
import { confirmar } from '../shared/nxDialogo.js';

// MARCA-COLORES-UNA-FUENTE-T1: el archivo del logo elegido en el asistente. No va a sessionStorage (es un archivo): si
// se recarga la pagina hay que elegirlo otra vez. Se sube al crear el negocio (la subida necesita su id).
let logoArchivo = null;

const STORAGE_KEY = 'nx_wizard_new_biz';
const STEP_NAMES = [
  'Datos básicos',
  'Ubicación y contacto',
  'Propietario',
  'Suscripción',
  'Config. operativa',
  'Apariencia',
  'Resumen',
];


// TIPO-NEGOCIO-CANON-T1: tipos y planes salen del config. Aqui habia una lista a
// mano con 7 tipos, cuando la ficha tenia 12: el wizard no dejaba crear una
// cafeteria. Se llenan al abrir el wizard; hasta entonces el catalogo esta vacio.
let catalogo = leerCatalogo(null);

// Defaults Nexo aplicados a apariencia (logo/fondo/colores) cuando el admin no los personaliza.
// Si el admin no cambia los valores en paso 6, se persisten estos defaults via updateAccountConfig.
// El Desktop (SincronizarAparienciaAsync en Funciones.vb) los descarga al arrancar.
export const NEXO_DEFAULTS = {
  logoUrl: '',
  fondoUrl: 'https://www.dropbox.com/scl/fi/bpglkpfgl0gkgfn5fdrow/Fondo-Nexo.jpg?rlkey=v4bqh6fpt25hf8thwucgm000g&dl=1',
  colorPrimario: '#238636',    // verde Nexo (theme-color del panel)
  colorSecundario: '#1f6feb',  // azul Nexo
  colorTexto: '#ffffff',
};

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

function getEmptyState() {
  const today = new Date().toISOString().slice(0, 10);
  return {
    step: 1,
    // Paso 1
    displayName: '', legalName: '', taxId: '', plan: 'Básico', businessType: '',
    // Paso 2 — Ubicación y contacto del negocio
    department: '', city: '',
    addressLine1: '', addressLine2: '', postalCode: '',
    phone: '', phoneHasWhatsapp: true, email: '', website: '',
    // Paso 3
    ownerName: '', ownerEmail: '', ownerPhone: '', ownerId: '',
    // Paso 4
    subscriptionPlan: 'Básico', subscriptionStart: today, duracionMeses: 1,
    // PANEL-WIZARD-SUBS-FECHA-PASADA-001: vencimiento por duracion (default) o
    // fecha exacta (acepta pasada hasta 5 dias de gracia, caso migracion).
    vencimientoModo: 'duracion', subscriptionEnd: '',
    // Paso 5
    umbralTardanza: 5, periodoGraciaDias: 7, asistenciaStrictMode: false,
    // Paso 6 — pre-poblado con defaults Nexo (admin puede sobreescribir)
    logoUrl: NEXO_DEFAULTS.logoUrl,
    fondoUrl: NEXO_DEFAULTS.fondoUrl,
    // Colores corporativos: no se editan en wizard (UX simple). Quedan como defaults Nexo
    // y se persisten via updateAccountConfig. Editables luego en NegocioDetailView/Apariencia.
    colorPrimario: NEXO_DEFAULTS.colorPrimario,
    colorSecundario: NEXO_DEFAULTS.colorSecundario,
    colorTexto: NEXO_DEFAULTS.colorTexto,
    // Paso 6 — FND-T1: marca del negocio (opcional). MARCA-COLORES-UNA-FUENTE-T1: el color de marca ES el color principal de la Apariencia.
    marcaDescripcion: '', marcaProductos: '', marcaValores: '', marcaColorHex: '', marcaColorNombre: '',
  };
}

/** FND-T1: la marca que escribio el admin en el paso 6, como la espera cuerpoDeMarca. */
function marcaDelWizard(s) {
  return {
    descripcion: s.marcaDescripcion, productosTipicos: s.marcaProductos, valores: s.marcaValores,
    colorHex: s.marcaColorHex, colorNombre: s.marcaColorNombre
  };
}

function loadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return getEmptyState();
    const parsed = JSON.parse(raw);
    return { ...getEmptyState(), ...parsed };
  } catch {
    return getEmptyState();
  }
}

function saveState(state) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

function clearState() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
}

function isValidEmail(s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim()); }
function isValidUrl(s) {
  if (!s) return true; // opcional
  try { new URL(s); return true; } catch { return false; }
}

function validateStep(state, step) {
  switch (step) {
    case 1:
      if (!state.displayName.trim()) return 'El nombre comercial es requerido';
      if (!state.legalName.trim()) return 'La razón social es requerida';
      if (!state.plan) return 'Selecciona un plan';
      if (!state.businessType) return 'Selecciona el tipo de negocio';
      if (state.taxId && !/^[\d.\-]{6,20}$/.test(state.taxId.trim())) return 'NIT/RUT inválido (solo dígitos, puntos o guiones, 6–20 caracteres)';
      return null;
    case 2:
      if (!state.department) return 'Selecciona un departamento';
      if (!state.city.trim()) return 'La ciudad es requerida';
      if (!state.addressLine1.trim()) return 'La dirección del negocio es requerida';
      if (!state.phone.trim()) return 'El teléfono del negocio es requerido';
      if (state.email.trim() && !isValidEmail(state.email)) return 'Email del negocio inválido';
      if (state.website.trim() && !isValidUrl(state.website)) return 'Sitio web inválido';
      return null;
    case 3:
      if (!state.ownerName.trim()) return 'El nombre del propietario es requerido';
      if (!isValidEmail(state.ownerEmail)) return 'Email inválido';
      if (!state.ownerPhone.trim()) return 'El teléfono es requerido';
      return null;
    case 4:
      if (!state.subscriptionPlan) return 'Selecciona un plan de suscripción';
      if (!state.subscriptionStart) return 'La fecha de inicio es requerida';
      if (state.vencimientoModo === 'fecha') {
        // Fecha exacta: protocolo Nexo acepta vencimiento pasado hasta 5 dias
        // (periodo de gracia). Mas atras ya no es una sub vigente.
        if (!state.subscriptionEnd) return 'La fecha de vencimiento es requerida';
        const end = new Date(state.subscriptionEnd + 'T12:00:00');
        const start = new Date(state.subscriptionStart + 'T12:00:00');
        const limiteGracia = new Date();
        limiteGracia.setHours(12, 0, 0, 0);
        limiteGracia.setDate(limiteGracia.getDate() - 5);
        if (isNaN(end.getTime())) return 'Fecha de vencimiento inválida';
        if (end < limiteGracia) return 'El vencimiento no puede ser anterior al período de gracia (máx. 5 días atrás)';
        if (end < start) return 'El vencimiento no puede ser anterior a la fecha de inicio';
      } else {
        if (!(Number(state.duracionMeses) > 0)) return 'La duración debe ser > 0';
      }
      return null;
    case 5:
      if (!(Number(state.umbralTardanza) >= 0)) return 'Umbral inválido';
      if (!(Number(state.periodoGraciaDias) >= 0)) return 'Periodo inválido';
      return null;
    case 6:
      if (!isValidUrl(state.logoUrl)) return 'URL de logo inválida';
      if (!isValidUrl(state.fondoUrl)) return 'URL de fondo inválida';
      return null;
    case 7:
      return null;
    default:
      return null;
  }
}

export function renderNegocioNuevoWizard(container) {
  // Gate por rol: solo admin
  if (AppConfig.role && AppConfig.role !== 'admin') {
    container.innerHTML = `
      <section class="artifact-view">
        ${regresoHtml({ volverA: "#/negocios", texto: "Negocios" })}
        <h1>Crear negocio nuevo</h1>
        <p class="empty-state" style="color:#f85149">Acceso restringido a administradores.</p>
      </section>`;
    return;
  }

  let state = loadState();

  container.innerHTML = `
    <section class="artifact-view wizard-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml({ volverA: "#/negocios", texto: "Negocios" })}
          <h1>Crear negocio nuevo</h1>
          <p class="subtitle">Wizard de 7 pasos · Progreso guardado automáticamente</p>
        </div>
        <button class="btn-ghost btn-sm" id="wizard-reset" title="Limpiar wizard y empezar de cero">Reiniciar</button>
      </div>

      <div class="wizard-progress" id="wizard-progress"></div>
      <div class="wizard-body" id="wizard-body"></div>
      <div class="wizard-footer">
        <button class="btn-ghost btn-sm" id="wizard-back">&larr; Atrás</button>
        <span class="wizard-error" id="wizard-error"></span>
        <button class="btn-primary btn-sm" id="wizard-next">Siguiente &rarr;</button>
      </div>
    </section>`;

  const $progress = document.getElementById('wizard-progress');
  const $body = document.getElementById('wizard-body');
  const $back = document.getElementById('wizard-back');
  const $next = document.getElementById('wizard-next');
  const $err = document.getElementById('wizard-error');

  const setField = (key, val) => {
    state[key] = val;
    saveState(state);
  };

  const renderProgress = () => {
    $progress.innerHTML = STEP_NAMES.map((n, i) => {
      const idx = i + 1;
      const cls = idx === state.step ? 'wizard-step wizard-step--active'
                : idx < state.step ? 'wizard-step wizard-step--done'
                : 'wizard-step';
      return `<div class="${cls}"><span class="wizard-step__num">${idx}</span><span class="wizard-step__label">${n}</span></div>`;
    }).join('');
  };

  const renderStep = () => {
    $err.textContent = '';
    $back.disabled = state.step === 1;
    if (state.step === 7) {
      $next.textContent = 'Crear negocio ✓';
    } else {
      $next.textContent = 'Siguiente →';
    }

    if (state.step === 1) {
      $body.innerHTML = `
        <div class="wizard-step-body">
          <h2 class="wizard-step-title">1. Datos básicos</h2>
          <div class="wizard-grid">
            <label class="wizard-field">
              <span>Nombre comercial *</span>
              <input id="f-displayName" class="form-input" value="${esc(state.displayName)}" placeholder="Ej: Panadería El Trigal" />
            </label>
            <label class="wizard-field">
              <span>Razón social *</span>
              <input id="f-legalName" class="form-input" value="${esc(state.legalName)}" placeholder="Ej: El Trigal S.A.S." />
            </label>
            <label class="wizard-field">
              <span>NIT / RUT</span>
              <input id="f-taxId" class="form-input" value="${esc(state.taxId)}" placeholder="Ej: 900123456-7" inputmode="numeric" />
            </label>
            <label class="wizard-field">
              <span>Plan *</span>
              <select id="f-plan" class="form-input">
                ${opcionesConValorActual(catalogo.planes, state.plan).map(p => `<option value="${esc(p)}"${state.plan === p ? ' selected' : ''}>${esc(p)}</option>`).join('')}
              </select>
            </label>
            <label class="wizard-field">
              <span>Tipo de negocio *</span>
              <select id="f-businessType" class="form-input">
                <option value="">— Seleccionar —</option>
                ${opcionesConValorActual(catalogo.tipos, state.businessType).map(t => `<option value="${esc(t)}"${state.businessType === t ? ' selected' : ''}>${esc(t)}</option>`).join('')}
              </select>
            </label>
          </div>
          ${catalogo.estado === 'ok' ? '' : `<p data-catalogo-aviso class="auth-error">${esc(MENSAJE_SIN_CATALOGO)}</p>`}
        </div>`;
      document.getElementById('f-displayName').addEventListener('input', e => setField('displayName', e.target.value));
      document.getElementById('f-legalName').addEventListener('input', e => setField('legalName', e.target.value));
      document.getElementById('f-taxId').addEventListener('input', e => setField('taxId', e.target.value));
      document.getElementById('f-plan').addEventListener('change', e => setField('plan', e.target.value));
      document.getElementById('f-businessType').addEventListener('change', e => setField('businessType', e.target.value));
    }

    if (state.step === 2) {
      const cities = CIUDADES[state.department] || [];
      $body.innerHTML = `
        <div class="wizard-step-body">
          <h2 class="wizard-step-title">2. Ubicación y contacto del negocio</h2>
          <div class="wizard-grid">
            <label class="wizard-field">
              <span>Departamento *</span>
              <select id="f-department" class="form-input">
                <option value="">— Seleccionar —</option>
                ${DEPARTAMENTOS.map(d => `<option value="${esc(d)}"${state.department === d ? ' selected' : ''}>${esc(d)}</option>`).join('')}
              </select>
            </label>
            <label class="wizard-field">
              <span>Ciudad *</span>
              ${cities.length > 0
                ? `<select id="f-city" class="form-input">
                    <option value="">— Seleccionar —</option>
                    ${cities.map(c => `<option value="${esc(c)}"${state.city === c ? ' selected' : ''}>${esc(c)}</option>`).join('')}
                    <option value="__otra__">Otra…</option>
                  </select>
                  <input id="f-city-free" class="form-input" style="margin-top:6px;display:${state.city && !cities.includes(state.city) ? '' : 'none'}" value="${esc(state.city && !cities.includes(state.city) ? state.city : '')}" placeholder="Nombre de ciudad" />`
                : `<input id="f-city" class="form-input" value="${esc(state.city)}" placeholder="Nombre de ciudad" />`}
            </label>
            <label class="wizard-field" style="grid-column:1/-1">
              <span>Dirección *</span>
              <input id="f-addressLine1" class="form-input" value="${esc(state.addressLine1)}" placeholder="Ej: Cra 39 # 3-53, o Barrio Centro" />
            </label>
            <label class="wizard-field" style="grid-column:1/-1">
              <span>Complemento / Referencia (opcional)</span>
              <input id="f-addressLine2" class="form-input" value="${esc(state.addressLine2)}" placeholder="Ej: Local 3, Cerca al parque…" />
            </label>
            <label class="wizard-field">
              <span>Código postal (opcional)</span>
              <input id="f-postalCode" class="form-input" value="${esc(state.postalCode)}" placeholder="Ej: 050001" />
            </label>
            <div class="wizard-field" style="grid-column:1/-1;margin-top:8px;padding-top:8px;border-top:1px solid #30363d">
              <span style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:.5px">Contacto del negocio (atención al cliente)</span>
            </div>
            <label class="wizard-field">
              <span>Teléfono del negocio *</span>
              <input id="f-phone" class="form-input" type="tel" value="${esc(state.phone)}" placeholder="+57 300 000 0000" />
              <label style="display:flex;align-items:center;gap:6px;margin-top:6px;font-size:0.875rem;color:#8b949e;cursor:pointer">
                <input id="f-phoneHasWhatsapp" type="checkbox" ${state.phoneHasWhatsapp ? 'checked' : ''} />
                Este teléfono tiene WhatsApp
              </label>
            </label>
            <label class="wizard-field">
              <span>Email del negocio (opcional)</span>
              <input id="f-email" class="form-input" type="email" value="${esc(state.email)}" placeholder="contacto@negocio.com" />
            </label>
            <label class="wizard-field" style="grid-column:1/-1">
              <span>Sitio web (opcional)</span>
              <input id="f-website" class="form-input" type="url" value="${esc(state.website)}" placeholder="https://" />
            </label>
          </div>
        </div>`;
      document.getElementById('f-department').addEventListener('change', e => {
        setField('department', e.target.value);
        setField('city', '');
        renderStep();
      });
      const $city = document.getElementById('f-city');
      if ($city?.tagName === 'SELECT') {
        $city.addEventListener('change', e => {
          const val = e.target.value;
          const $free = document.getElementById('f-city-free');
          if (val === '__otra__') {
            if ($free) $free.style.display = '';
            setField('city', $free?.value || '');
          } else {
            if ($free) $free.style.display = 'none';
            setField('city', val);
          }
        });
        document.getElementById('f-city-free')?.addEventListener('input', e => setField('city', e.target.value));
      } else if ($city) {
        $city.addEventListener('input', e => setField('city', e.target.value));
      }
      // Nuevos campos contacto + direccion (PARIDAD REDIRECT)
      ['addressLine1', 'addressLine2', 'postalCode', 'phone', 'email', 'website'].forEach(k => {
        document.getElementById(`f-${k}`)?.addEventListener('input', e => setField(k, e.target.value));
      });
      document.getElementById('f-phoneHasWhatsapp')?.addEventListener('change', e => setField('phoneHasWhatsapp', e.target.checked));
    }

    if (state.step === 3) {
      $body.innerHTML = `
        <div class="wizard-step-body">
          <h2 class="wizard-step-title">3. Propietario</h2>
          <div class="wizard-grid">
            <label class="wizard-field">
              <span>Nombre completo *</span>
              <input id="f-ownerName" class="form-input" value="${esc(state.ownerName)}" />
            </label>
            <label class="wizard-field">
              <span>Email *</span>
              <input id="f-ownerEmail" type="email" class="form-input" value="${esc(state.ownerEmail)}" placeholder="propietario@ejemplo.com" />
            </label>
            <label class="wizard-field">
              <span>Teléfono *</span>
              <input id="f-ownerPhone" class="form-input" value="${esc(state.ownerPhone)}" placeholder="+57 300 000 0000" />
            </label>
            <label class="wizard-field">
              <span>Documento (cédula/NIT)</span>
              <input id="f-ownerId" class="form-input" value="${esc(state.ownerId)}" />
            </label>
          </div>
        </div>`;
      ['ownerName', 'ownerEmail', 'ownerPhone', 'ownerId'].forEach(k => {
        document.getElementById(`f-${k}`).addEventListener('input', e => setField(k, e.target.value));
      });
    }

    if (state.step === 4) {
      // PANEL-WIZARD-SUBS-FECHA-PASADA-001: modo dual de vencimiento. 'duracion'
      // (default, comportamiento previo) calcula start+N meses; 'fecha' permite
      // fecha EXACTA editable, incluida pasada hasta 5 dias (periodo de gracia
      // Nexo) para migrar negocios cuya sub real ya vencio. El wizard NO la
      // recalcula ni la modifica.
      const esFecha = state.vencimientoModo === 'fecha';
      $body.innerHTML = `
        <div class="wizard-step-body">
          <h2 class="wizard-step-title">4. Suscripción</h2>
          <div class="wizard-grid">
            <label class="wizard-field">
              <span>Plan de suscripción *</span>
              <select id="f-subscriptionPlan" class="form-input">
                ${opcionesConValorActual(catalogo.planes, state.subscriptionPlan).map(p => `<option value="${esc(p)}"${state.subscriptionPlan === p ? ' selected' : ''}>${esc(p)}</option>`).join('')}
              </select>
            </label>
            <label class="wizard-field">
              <span>Fecha de inicio *</span>
              <input id="f-subscriptionStart" type="date" class="form-input" value="${esc(state.subscriptionStart)}" />
            </label>
            <label class="wizard-field">
              <span>Vencimiento *</span>
              <select id="f-vencimientoModo" class="form-input">
                <option value="duracion"${esFecha ? '' : ' selected'}>Por duración (meses)</option>
                <option value="fecha"${esFecha ? ' selected' : ''}>Fecha exacta</option>
              </select>
            </label>
            ${esFecha ? `
            <label class="wizard-field">
              <span>Fecha de vencimiento *</span>
              <input id="f-subscriptionEnd" type="date" class="form-input" value="${esc(state.subscriptionEnd)}" />
            </label>` : `
            <label class="wizard-field">
              <span>Duración (meses) *</span>
              <span style="font-weight:400;color:#8b949e;font-size:0.875rem">Por defecto 1 mes. Si el cliente paga más, cámbialo aquí.</span>
              <input id="f-duracionMeses" type="number" min="1" max="60" class="form-input" value="${esc(state.duracionMeses)}" />
            </label>`}
          </div>
          <p style="color:#8b949e;font-size:0.875rem;margin-top:8px">Fecha de vencimiento: <strong>${esc(effectiveEndDate(state))}</strong>${esFecha ? ' <span style="color:#6e7681">(exacta — se acepta hasta 5 días atrás, período de gracia)</span>' : ' <span style="color:#6e7681">(calculada)</span>'}</p>
        </div>`;
      document.getElementById('f-subscriptionPlan').addEventListener('change', e => setField('subscriptionPlan', e.target.value));
      document.getElementById('f-subscriptionStart').addEventListener('change', e => { setField('subscriptionStart', e.target.value); renderStep(); });
      document.getElementById('f-vencimientoModo').addEventListener('change', e => { setField('vencimientoModo', e.target.value); renderStep(); });
      document.getElementById('f-duracionMeses')?.addEventListener('input', e => { setField('duracionMeses', Number(e.target.value)); renderStep(); });
      document.getElementById('f-subscriptionEnd')?.addEventListener('change', e => { setField('subscriptionEnd', e.target.value); renderStep(); });
    }

    if (state.step === 5) {
      $body.innerHTML = `
        <div class="wizard-step-body">
          <h2 class="wizard-step-title">5. Configuración operativa</h2>
          <div class="wizard-grid">
            <label class="wizard-field">
              <span>Umbral tardanza (minutos)</span>
              <input id="f-umbralTardanza" type="number" min="0" max="120" class="form-input" value="${esc(state.umbralTardanza)}" />
            </label>
            <label class="wizard-field">
              <span>Periodo de gracia (días)</span>
              <input id="f-periodoGraciaDias" type="number" min="0" max="60" class="form-input" value="${esc(state.periodoGraciaDias)}" />
            </label>
            <label class="wizard-field" style="flex-direction:row;align-items:center;gap:10px">
              <input id="f-asistenciaStrictMode" type="checkbox" ${state.asistenciaStrictMode ? 'checked' : ''} />
              <span>Modo estricto de asistencia</span>
            </label>
          </div>
          <p style="color:#8b949e;font-size:0.875rem;margin-top:8px">Estos valores se guardan en la config operativa (<code>/api/v1/businesses/{id}/ops-config</code>) después de crear el negocio.</p>
        </div>`;
      document.getElementById('f-umbralTardanza').addEventListener('input', e => setField('umbralTardanza', Number(e.target.value)));
      document.getElementById('f-periodoGraciaDias').addEventListener('input', e => setField('periodoGraciaDias', Number(e.target.value)));
      document.getElementById('f-asistenciaStrictMode').addEventListener('change', e => setField('asistenciaStrictMode', e.target.checked));
    }

    if (state.step === 6) {
      $body.innerHTML = `
        <div class="wizard-step-body">
          <h2 class="wizard-step-title">6. Apariencia (opcional)</h2>
          <div class="wizard-grid">
            <label class="wizard-field">
              <span>URL del logo</span>
              <input id="f-logoUrl" type="url" class="form-input" value="${esc(state.logoUrl)}" placeholder="https://..." />
            </label>
            <label class="wizard-field">
              <span>URL de fondo</span>
              <input id="f-fondoUrl" type="url" class="form-input" value="${esc(state.fondoUrl)}" placeholder="https://..." />
            </label>
            <div class="wizard-field" style="grid-column:1/-1" data-wizard-logo-archivo>
              <span>…o sube el archivo del logo (PNG o JPG, maximo 5 MB; se sube al crear el negocio)</span>
              <input id="f-logoArchivo" type="file" class="form-input" accept="image/png,image/jpeg" />
              <span id="f-logoArchivo-estado" style="font-size:0.875rem;color:#8b949e">${logoArchivo ? 'Elegido: ' + esc(logoArchivo.name) : ''}</span>
            </div>
            <div class="wizard-field" style="grid-column:1/-1" data-wizard-gotero>
              <span>Color de la marca: toca un color del logo o elige una muestra</span>
              <div style="display:flex;flex-wrap:wrap;gap:12px;align-items:flex-start">
                <canvas id="f-logoCanvas" width="240" height="120" title="Toca el logo para tomar su color"
                  style="max-width:100%;background:#fff;border:1px solid #30363d;border-radius:6px;cursor:crosshair;display:${logoArchivo ? 'block' : 'none'}"></canvas>
                <div style="display:flex;flex-direction:column;gap:6px;min-width:0">
                  <div id="f-muestras" style="display:flex;flex-wrap:wrap;gap:6px"></div>
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.875rem;color:#8b949e">o con el selector
                    <input id="f-colorPicker" type="color" value="${esc(deHex(state.marcaColorHex) ? state.marcaColorHex : '#238636')}" style="width:40px;height:28px;padding:0;border:none;background:none" />
                  </label>
                </div>
              </div>
              <span style="font-size:0.875rem;color:#8b949e">Es el color principal del negocio: el del Desktop y el de los fondos (uno solo).</span>
            </div>
          </div>
          <p style="color:#8b949e;font-size:0.875rem;margin-top:8px">Puedes dejar en blanco y editar desde Apariencia después.</p>
          <h3 class="wizard-step-title" style="font-size:.95rem;margin-top:20px">Marca del negocio</h3>
          <p style="color:#8b949e;font-size:0.875rem;margin:0 0 10px">Opcional aqui: se completa despues en la ficha, pestana Info. Ejemplos: ${EJEMPLOS.map(esc).join(' &middot; ')}</p>
          <div class="wizard-grid" data-wizard-marca>
            <label class="wizard-field" style="grid-column:1/-1">
              <span>${ETIQUETAS.descripcion}</span>
              <input id="f-marcaDescripcion" type="text" class="form-input" value="${esc(state.marcaDescripcion)}" placeholder="Ej.: restaurante de comida mexicana" />
            </label>
            <label class="wizard-field" style="grid-column:1/-1">
              <span>${ETIQUETAS.productosTipicos} &middot; separados por coma</span>
              <input id="f-marcaProductos" type="text" class="form-input" value="${esc(state.marcaProductos)}" placeholder="tacos, burritos, quesadillas" />
            </label>
            <label class="wizard-field">
              <span>${ETIQUETAS.valores} &middot; separados por coma</span>
              <input id="f-marcaValores" type="text" class="form-input" value="${esc(state.marcaValores)}" placeholder="sabor casero, alegria" />
            </label>
            <label class="wizard-field">
              <span>${ETIQUETAS.colorHex} (#RRGGBB y nombre)</span>
              <span style="display:flex;gap:8px">
                <input id="f-marcaColorHex" type="text" class="form-input" value="${esc(state.marcaColorHex)}" placeholder="#E31837" style="flex:none;width:12ch;max-width:12ch;min-width:0" />
                <input id="f-marcaColorNombre" type="text" class="form-input" value="${esc(state.marcaColorNombre)}" placeholder="rojo tomate" style="min-width:0;flex:1" />
              </span>
            </label>
          </div>
        </div>`;
      document.getElementById('f-logoUrl').addEventListener('input', e => setField('logoUrl', e.target.value));
      document.getElementById('f-fondoUrl').addEventListener('input', e => setField('fondoUrl', e.target.value));
      for (const k of ['marcaDescripcion', 'marcaProductos', 'marcaValores', 'marcaColorHex', 'marcaColorNombre']) {
        document.getElementById('f-' + k).addEventListener('input', e => setField(k, e.target.value));
      }
      montarGotero(setField);
    }

    if (state.step === 7) {
      $body.innerHTML = renderSummary(state);
    }

    renderProgress();
  };

  const goto = (step) => {
    state.step = Math.max(1, Math.min(7, step));
    saveState(state);
    renderStep();
  };

  $back.addEventListener('click', () => { if (state.step > 1) goto(state.step - 1); });

  $next.addEventListener('click', async () => {
    $err.textContent = '';
    const err = validateStep(state, state.step);
    if (err) { $err.textContent = err; return; }
    if (state.step < 7) {
      goto(state.step + 1);
      return;
    }
    // Paso 7: crear
    $next.disabled = true;
    $next.textContent = 'Creando…';
    const res = await submitWizard(state);
    if (res.ok) {
      clearState();
      // Fail-loud suave: el negocio quedo, pero si la sub fallo se avisa (antes
      // el catch vacio ocultaba el fallo y la fecha "desaparecia" sin pista).
      // Puede haber mas de un aviso (suscripcion y/o propietario): se muestran
      // todos. Tragarse uno seria dejar el negocio a medias en silencio.
      const avisos = [res.subWarning, res.colabWarning, res.marcaWarning, res.logoWarning].filter(Boolean);
      showToast(avisos.length ? '⚠ ' + avisos.join('  —  ') : 'Negocio creado ✓');
      window.location.hash = `#/negocios/${res.bizId}`;
    } else {
      $next.disabled = false;
      $next.textContent = 'Crear negocio ✓';
      $err.textContent = `Error al crear: ${res.error || 'intenta de nuevo'}`;
    }
  });

  document.getElementById('wizard-reset').addEventListener('click', async () => {
    if (!await confirmar({ titulo: 'Limpiar el asistente', texto: 'Se borra todo lo escrito y se empieza de cero.', aceptar: 'Empezar de cero', peligro: true })) return;
    clearState();
    state = getEmptyState();
    goto(1);
  });

  renderStep();

  // TIPO-NEGOCIO-CANON-T1: la lista de tipos y planes llega del config y el paso
  // se vuelve a pintar con ella. Mientras llega, el select solo tiene el valor
  // que ya estaba escrito (nunca una lista vieja del panel).
  BusinessApiClient.getConfig().then((res) => {
    catalogo = leerCatalogo(res);
    if (state.step === 1 || state.step === 4) renderStep();
  });
}

function computeEndDate(startIso, months) {
  if (!startIso || !months) return '—';
  const d = new Date(startIso + 'T12:00:00');
  if (isNaN(d.getTime())) return '—';
  d.setMonth(d.getMonth() + Number(months));
  return d.toISOString().slice(0, 10);
}

/**
 * PANEL-WIZARD-SUBS-FECHA-PASADA-001: vencimiento efectivo de la sub del wizard.
 * Modo 'fecha' devuelve la fecha EXACTA elegida (sin recalcular); modo 'duracion'
 * mantiene el calculo start + N meses.
 */
function effectiveEndDate(s) {
  return s.vencimientoModo === 'fecha'
    ? (s.subscriptionEnd || '—')
    : computeEndDate(s.subscriptionStart, s.duracionMeses);
}

/**
 * MARCA-COLORES-UNA-FUENTE-T1 paso 4: el logo elegido se dibuja en un canvas; de sus pixeles salen las muestras
 * (coloresDominantes) y un toque sobre el logo toma el color exacto (gotero). Lo elegido va al campo de color de marca,
 * que en el alta es tambien el color principal de la Apariencia (una sola fuente).
 */
function montarGotero(setField) {
  const $input = document.getElementById('f-logoArchivo');
  const $estado = document.getElementById('f-logoArchivo-estado');
  const $canvas = document.getElementById('f-logoCanvas');
  const $muestras = document.getElementById('f-muestras');
  const $picker = document.getElementById('f-colorPicker');
  const $hex = document.getElementById('f-marcaColorHex');
  if (!$input || !$canvas || !$muestras) return;
  const ctx = $canvas.getContext('2d', { willReadFrequently: true });

  const elegir = (hex) => {
    if (!deHex(hex)) return;
    if ($hex) $hex.value = hex;
    if ($picker) $picker.value = hex;
    setField('marcaColorHex', hex);
    $muestras.querySelectorAll('[data-muestra]').forEach((b) => { b.style.outline = b.dataset.muestra === hex ? '2px solid #58a6ff' : 'none'; });
  };

  const dibujar = async (archivo) => {
    let bitmap;
    try { bitmap = await createImageBitmap(archivo); } catch { $estado.textContent = 'No pude leer esa imagen.'; return; }
    const escala = Math.min($canvas.width / bitmap.width, $canvas.height / bitmap.height, 1);
    const w = Math.max(1, Math.round(bitmap.width * escala)), h = Math.max(1, Math.round(bitmap.height * escala));
    $canvas.width = w; $canvas.height = h;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    $canvas.style.display = 'block';
    const colores = coloresDominantes(ctx.getImageData(0, 0, w, h).data, 5);
    $muestras.innerHTML = colores.map((c) => `<button type="button" data-muestra="${c}" title="${c}" aria-label="Usar ${c}"
      style="width:28px;height:28px;border-radius:6px;border:1px solid #30363d;background:${c};cursor:pointer"></button>`).join('');
    $muestras.querySelectorAll('[data-muestra]').forEach((b) => b.addEventListener('click', () => elegir(b.dataset.muestra)));
    if (colores[0] && !deHex($hex?.value)) elegir(colores[0]);
  };

  $input.addEventListener('change', async () => {
    const archivo = $input.files?.[0];
    if (!archivo) return;
    let comienzo = null;
    try { comienzo = new Uint8Array(await archivo.slice(0, 8).arrayBuffer()); } catch { /* que decida el API */ }
    const no = porQueNoSePuedeSubir(archivo, 'logo', comienzo);
    if (no) { $estado.textContent = no; $estado.style.color = '#f85149'; $input.value = ''; return; }
    logoArchivo = archivo;
    $estado.textContent = 'Elegido: ' + archivo.name + '. Se sube al crear el negocio.';
    $estado.style.color = '#3fb950';
    await dibujar(archivo);
  });
  $canvas.addEventListener('click', (e) => {
    const r = $canvas.getBoundingClientRect();
    const x = (e.clientX - r.left) * ($canvas.width / r.width);
    const y = (e.clientY - r.top) * ($canvas.height / r.height);
    const c = colorEnPunto(ctx.getImageData(0, 0, $canvas.width, $canvas.height).data, $canvas.width, $canvas.height, x, y);
    if (c) elegir(c);
  });
  $picker?.addEventListener('input', () => elegir($picker.value));
  if (logoArchivo) dibujar(logoArchivo);
}

function renderSummary(s) {
  const row = (label, value) => `
    <tr><td class="wizard-summary__label">${esc(label)}</td>
        <td class="wizard-summary__value">${esc(value || '—')}</td></tr>`;
  return `
    <div class="wizard-step-body">
      <h2 class="wizard-step-title">7. Resumen</h2>
      <p style="color:#8b949e;font-size:0.875rem;margin-bottom:12px">Revisa los datos antes de crear. Puedes volver a editar con el botón Atrás.</p>
      <table class="wizard-summary">
        <tbody>
          ${row('Nombre comercial', s.displayName)}
          ${row('Razón social', s.legalName)}
          ${row('NIT / RUT', s.taxId)}
          ${row('Plan', s.plan)}
          ${row('Ubicación', `${s.department}${s.city ? ' · ' + s.city : ''}`)}
          ${row('Dirección', [s.addressLine1, s.addressLine2].filter(Boolean).join(' · '))}
          ${row('Código postal', s.postalCode)}
          ${row('Teléfono negocio', `${s.phone || ''}${s.phone && s.phoneHasWhatsapp ? ' · WhatsApp' : ''}`)}
          ${row('Email negocio', s.email)}
          ${row('Sitio web', s.website)}
          ${row('Propietario', s.ownerName)}
          ${row('Email propietario', s.ownerEmail)}
          ${row('Teléfono propietario', s.ownerPhone)}
          ${row('Documento propietario', s.ownerId)}
          ${row('Suscripción', `${s.subscriptionPlan} · ${s.subscriptionStart} → ${effectiveEndDate(s)}${s.vencimientoModo === 'fecha' ? ' (fecha exacta)' : ` (${s.duracionMeses} meses)`}`)}
          ${row('Umbral tardanza', `${s.umbralTardanza} min`)}
          ${row('Periodo gracia', `${s.periodoGraciaDias} días`)}
          ${row('Modo estricto', s.asistenciaStrictMode ? 'Sí' : 'No')}
          ${row('Logo', s.logoUrl)}
          ${row('Fondo', s.fondoUrl)}
          ${row('Marca', marcaVacia(marcaDelWizard(s)) ? 'sin escribir (se completa en la ficha)'
            : (loQueFalta(marcaDelWizard(s)).length ? 'a medias, falta: ' + loQueFalta(marcaDelWizard(s)).map((f) => ETIQUETAS[f] || f).join(', ') : 'completa') + ' · ' + (s.marcaDescripcion || ''))}
        </tbody>
      </table>
    </div>`;
}

async function submitWizard(s) {
  // 1. Crear negocio (POST /api/businesses)
  // PANEL-WIZARD-PARIDAD-NEGOCIO-001: paridad wizard ↔ tabs
  //   - taxId: top-level (NIT/RUT) — backend lo persiste en columna tax_id
  //   - state: top-level (departamento) — alinea con DTO que usa "State"
  //   - phone/email/website/addressLine1/addressLine2/postalCode: top-level (tab Contacto)
  //   - metadata: datos del propietario, tab Propietario los lee con metaStr(b, ownerX)
  //   - logoUrl/fondoUrl/colores: NO en POST create (el DTO los ignora) — van via /account-config
  const ownerCedula = (s.ownerId || '').trim();
  const createPayload = {
    displayName: s.displayName,
    legalName: s.legalName,
    taxId: (s.taxId || '').trim() || null,
    plan: s.plan,
    businessType: s.businessType,
    state: s.department,
    city: s.city,
    addressLine1: (s.addressLine1 || '').trim() || null,
    addressLine2: (s.addressLine2 || '').trim() || null,
    postalCode: (s.postalCode || '').trim() || null,
    phone: (s.phone || '').trim() || null,
    email: (s.email || '').trim() || null,
    website: (s.website || '').trim() || null,
    metadata: {
      ownerName: s.ownerName || null,
      ownerEmail: s.ownerEmail || null,
      ownerPhone: s.ownerPhone || null,
      ownerCedula: ownerCedula || null,
      phoneHasWhatsapp: !!s.phoneHasWhatsapp,
    },
  };
  const createRes = await BusinessApiClient.create(createPayload);
  if (!createRes.ok) {
    return { ok: false, error: createRes.error || 'No se pudo crear el negocio' };
  }
  const created = createRes.data?.data || createRes.data || {};
  const bizId = created.businessId || created.id || created.business_id;
  if (!bizId) return { ok: false, error: 'La API no retornó businessId' };

  // 1.5 — Guardar apariencia en account_config. CreateBusinessDto NO acepta logoUrl/fondoUrl/colores
  // (BusinessesController.cs:1059), por lo que el POST anterior los ignora. Hay que persistir aqui
  // via PATCH /account-config. Se envia siempre (con defaults Nexo si admin no cambio nada).
  // MARCA-COLORES-UNA-FUENTE-T1: el logo elegido como archivo se sube ahora (la subida necesita el id del negocio); si
  // falla, queda el link si lo habia y se avisa al final. El color principal es el de la marca (una sola fuente).
  let logoUrl = s.logoUrl || null;
  let logoWarning = null;
  if (logoArchivo) {
    try {
      const primeros = new Uint8Array(await logoArchivo.slice(0, 8).arrayBuffer());
      const sub = await AparienciaApiClient.subirArchivo(bizId, 'logo', logoArchivo, primeros);
      if (sub.ok && sub.url) { logoUrl = sub.url; logoArchivo = null; }
      else logoWarning = 'El logo no se pudo subir (' + (sub.error || 'error') + '). Subelo en Apariencia.';
    } catch (err) { logoWarning = 'El logo no se pudo subir. Subelo en Apariencia.'; }
  }
  try {
    await BusinessApiClient.updateAccountConfig(bizId, {
      logoUrl,
      fondoUrl: s.fondoUrl || null,
      colorPrimario: colorPrincipalDelAlta(s.marcaColorHex, s.colorPrimario) || null,
      colorSecundario: s.colorSecundario || null,
      colorTexto: s.colorTexto || null,
    });
  } catch { /* no bloquea — admin puede editar luego en NegocioDetailView/Apariencia */ }

  // 2. Guardar ops-config (best-effort)
  try {
    await BusinessApiClient.updateOpsConfig(bizId, {
      umbral_tardanza_minutos: Number(s.umbralTardanza),
      periodo_gracia_dias: Number(s.periodoGraciaDias),
      asistencia_strict_mode: !!s.asistenciaStrictMode,
    });
  } catch { /* no bloquea */ }

  // 3. Suscripcion — ROOT-CAUSE FIX (PANEL-WIZARD-SUBS-FECHA-PASADA-001): este
  // POST iba a /api/v1/businesses/... (404: BusinessesController es api/Businesses
  // SIN v1) con durationMonths (campo inexistente en CreateSubscriptionRequest).
  // El catch vacio lo tragaba: la sub del wizard NUNCA se creo y la fecha elegida
  // jamas llegaba a BD. Ahora: SubscriptionApiClient (ruta correcta) + payload del
  // DTO real con endDate efectiva (exacta o calculada) + warning fail-loud.
  let subWarning = null;
  try {
    const { SubscriptionApiClient } = await import('../../infrastructure/api/SubscriptionApiClient.js');
    const endDate = effectiveEndDate(s);
    const subRes = await SubscriptionApiClient.create(bizId, {
      plan: s.subscriptionPlan,
      status: 'active',
      billingCycle: 'monthly',
      amount: 0,
      currency: 'COP',
      startDate: s.subscriptionStart,
      endDate: endDate && endDate !== '—' ? endDate : undefined,
      notes: s.vencimientoModo === 'fecha'
        ? 'Creada por wizard (vencimiento exacto, posible migracion en gracia)'
        : `Creada por wizard (${s.duracionMeses} meses)`
    });
    if (!subRes.ok) {
      subWarning = 'Negocio creado, pero la suscripción fallo: ' +
        ((typeof subRes.error === 'string' ? subRes.error : subRes.error?.message) || 'error API') +
        '. Creala manualmente en la pestana Suscripcion.';
    }
  } catch (err) {
    subWarning = 'Negocio creado, pero la suscripción fallo: ' + (err?.message || 'error inesperado') +
      '. Creala manualmente en la pestana Suscripcion.';
  }

  // 4. CS-A1: el PROPIETARIO queda como colaborador del negocio.
  //
  // Sin esto un negocio nace SIN NINGUN colaborador. El sync baja 0 usuarios y
  // Nexo Desktop, al no encontrar a nadie, deja entrar la sesion anonima como
  // Propietario con los 14 permisos en "Si" (informe CS-R6, PR #278). Medido en
  // Postgres el 31-ago-2026: 30 de 31 negocios estaban asi, 28 de ellos activos.
  // El ColaboradoresIdentidadSyncService que YA corre en el primer arranque lo
  // baja solo, asi que esto ataca la causa SIN tocar Desktop.
  //
  // Best-effort como los pasos de arriba: el negocio ya esta creado y no se
  // deshace, pero el aviso es fuerte a proposito — un negocio sin propietario
  // es justo el caso que deja la puerta abierta.
  let colabWarning = null;
  try {
    const { ColaboradorApiClient } = await import('../../infrastructure/api/ColaboradorApiClient.js');
    // Claves camelCase, igual que ColaboradoresView (ligan con el DTO PascalCase
    // CreateColaboradorRequest). NombreCompleto es su unico [Required] y el paso 3
    // del wizard ya lo exige, asi que aqui no puede llegar vacio.
    const colabRes = await ColaboradorApiClient.create(bizId, {
      nombreCompleto: (s.ownerName || '').trim(),
      documento: ownerCedula || null,
      telefono: (s.ownerPhone || '').trim() || null,
      cargo: 'Propietario',
      area: 'Administracion',
      fechaIngreso: new Date().toISOString().slice(0, 10),
    });
    if (!colabRes.ok) {
      colabWarning = 'Negocio creado, pero el PROPIETARIO no quedo como colaborador: ' +
        ((typeof colabRes.error === 'string' ? colabRes.error : colabRes.error?.message) || 'error API') +
        '. Crealo en Colaboradores ANTES de abrir Nexo Desktop.';
    }
  } catch (err) {
    colabWarning = 'Negocio creado, pero el PROPIETARIO no quedo como colaborador: ' +
      (err?.message || 'error inesperado') +
      '. Crealo en Colaboradores ANTES de abrir Nexo Desktop.';
  }

  // 5. FND-T1: la marca del negocio, si el admin escribio algo en el paso 6. Best-effort como la apariencia: el negocio
  // ya existe; si no se guardo, el aviso dice donde completarla. Una marca a medias se guarda y se dice que falta.
  let marcaWarning = null;
  const marca = marcaDelWizard(s);
  if (!marcaVacia(marca)) {
    try {
      const mRes = await BusinessApiClient.guardarMarca(bizId, cuerpoDeMarca(marca));
      if (!mRes.ok) marcaWarning = 'La marca no se guardo: ' + motivoDeLaMarcaDelNegocio(mRes).frase + ' Completala en la ficha, pestana Info.';
      else {
        const falta = loQueFalta(marca);
        if (falta.length) marcaWarning = 'Marca guardada a medias; falta: ' + falta.map((f) => ETIQUETAS[f] || f).join(', ') + '.';
      }
    } catch (err) {
      marcaWarning = 'La marca no se guardo: ' + (err?.message || 'error inesperado') + '. Completala en la ficha, pestana Info.';
    }
  }

  return { ok: true, bizId, subWarning, colabWarning, marcaWarning, logoWarning };
}

function showToast(msg) {
  const toast = document.createElement('div');
  toast.className = 'reward-toast';
  toast.textContent = msg;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add('visible'));
  setTimeout(() => { toast.classList.remove('visible'); setTimeout(() => toast.remove(), 400); }, 3000);
}
